import { useCallback, useEffect, useMemo, useState } from "react";
import type { UseFormSetValue, FieldPath, FieldValues } from "react-hook-form";
import type { DropdownMediaOption } from "@model/UIComponents.model";
import { Spec } from "@model/Category.model";


export type Category = {
  id: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
};

type Options<TFormValues extends FieldValues> = {
  categories: Category[];
  setValue: UseFormSetValue<TFormValues>;
  specsFieldName?: FieldPath<TFormValues>;
  initialSpecs?: Spec[];
};

export function useProductFormLogic<TFormValues extends FieldValues>({
  categories,
  setValue,
  specsFieldName,
  initialSpecs = [{ label: "", value: "" }],
}: Options<TFormValues>) {
  const categoryOptions: DropdownMediaOption[] = useMemo(
    () =>
      categories.map((c) => ({
        value: c.id,
        label: c.title,
        description: c.description ?? null,
        imageUrl: c.imageUrl ?? null,
      })),
    [categories]
  );

  const [specs, setSpecs] = useState<Spec[]>(initialSpecs);

  const addSpec = useCallback(() => {
    setSpecs((s) => [...s, { label: "", value: "" }]);
  }, []);

  const removeSpecAt = useCallback((idx: number) => {
    setSpecs((s) => s.filter((_, i) => i !== idx));
  }, []);

  const updateSpec = useCallback((idx: number, patch: Partial<Spec>) => {
    setSpecs((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }, []);

  const resetSpecs = useCallback((rows: Spec[] = [{ label: "", value: "" }]) => {
    setSpecs(rows);
  }, []);

  useEffect(() => {
    if (!specsFieldName) return; 
    setValue(
      specsFieldName,
      specs.map((s) => ({ label: s.label, value: s.value })) as any,
      { shouldDirty: true, shouldTouch: false, shouldValidate: false }
    );
  }, [specs, setValue, specsFieldName]);

  return {
    categoryOptions,
    specs,
    addSpec,
    removeSpecAt,
    updateSpec,
    resetSpecs,
    setSpecs, 
  };
}

export default useProductFormLogic;
